import { useState } from "react";
import { motion } from "framer-motion";
import { Search, CheckCircle2, Trash2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function BookingLookup() {
  const [ref, setRef] = useState("");
  const [booking, setBooking] = useState(null);
  const [cancelled, setCancelled] = useState(false);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setCancelled(false);
    try {
      const r = await api.get(`/bookings/by-ref/${encodeURIComponent(ref.trim())}`);
      setBooking(r.data);
    } catch (err) {
      setBooking(null);
      toast.error(err.response?.data?.detail || "No booking found with that reference");
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    try {
      await api.delete(`/bookings/by-ref/${encodeURIComponent(booking.ref)}`);
      setCancelled(true);
      setBooking(null);
      toast.success("Booking cancelled — the slot is free again");
    } catch {
      toast.error("Could not cancel — try again");
    }
  };

  return (
    <main className="max-w-3xl mx-auto px-6 lg:px-10 py-20 min-h-[70vh]" data-testid="booking-lookup-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <p className="text-[#ccff00] uppercase tracking-[0.3em] text-sm mb-4">My Booking</p>
        <h1 className="font-display uppercase text-4xl sm:text-5xl tracking-tight">
          Find your <span className="text-[#ccff00]">court time.</span>
        </h1>
        <p className="text-white/60 mt-6 leading-relaxed">
          Enter the PSH reference from your booking confirmation to view or cancel your demo booking.
        </p>
      </motion.div>

      <form onSubmit={lookup} className="flex flex-col sm:flex-row gap-3 mt-10">
        <input
          required
          value={ref}
          onChange={(e) => setRef(e.target.value.toUpperCase())}
          placeholder="PSH-XXXXXX"
          data-testid="lookup-ref-input"
          aria-label="Booking reference"
          className="flex-1 min-w-0 bg-[#1a1a1a] border border-white/15 rounded-full px-6 py-4 font-mono tracking-widest focus:outline-none focus:border-[#ccff00]/60 placeholder:text-white/30"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="lookup-submit-btn"
          className="w-full sm:w-auto shrink-0 bg-[#ccff00] text-black font-bold uppercase tracking-wider px-8 py-4 rounded-full hover:bg-[#a3e635] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          <Search size={16} /> {loading ? "Searching…" : "Find"}
        </button>
      </form>

      {booking && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-10 bg-[#0f0f10] border border-[#ccff00]/40 shadow-[0_0_20px_rgba(204,255,0,0.12)] rounded-md p-8"
          data-testid="lookup-result-card"
        >
          <div className="flex items-center gap-3 mb-6">
            <CalendarDays className="text-[#ccff00]" size={24} />
            <p className="text-[#ccff00] font-mono text-xl" data-testid="lookup-result-ref">{booking.ref}</p>
          </div>
          <div className="space-y-2 text-white/80">
            <p><span className="text-white/50">Court:</span> {booking.court_name}</p>
            <p><span className="text-white/50">When:</span> {booking.date} at {booking.time}</p>
            <p><span className="text-white/50">Player:</span> {booking.name}</p>
            <p><span className="text-white/50">Rate:</span> ${booking.price}/hr (simulated)</p>
          </div>
          <button
            onClick={cancel}
            data-testid="lookup-cancel-btn"
            className="mt-8 border border-red-500/50 text-red-400 uppercase tracking-wider font-semibold px-8 py-3 rounded-full hover:bg-red-500/10 transition-colors inline-flex items-center gap-2"
          >
            <Trash2 size={16} /> Cancel this booking
          </button>
        </motion.div>
      )}

      {cancelled && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-10 bg-[#0f0f10] border border-[#2a2a2a] rounded-md p-8 text-center" data-testid="lookup-cancelled-card">
          <CheckCircle2 className="mx-auto text-[#ccff00] mb-4" size={44} />
          <p className="font-display uppercase text-2xl tracking-tight">Booking cancelled</p>
          <p className="text-white/60 mt-2">The slot is back on the calendar. See you on court another time.</p>
        </motion.div>
      )}
    </main>
  );
}
